import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { TranslateWithFormatDirective } from 'app/shared/directives/translate-with-format.directive';
import { CookieConsentService } from 'app/shared/services/cookie-consent.service';

@Component({
  selector: 'kh-cookie-consent-banner',
  templateUrl: './cookie-consent-banner.component.html',
  styleUrls: ['./cookie-consent-banner.component.less'],
  standalone: true,
  imports: [TranslateModule, TranslateWithFormatDirective]
})
export class CookieConsentBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  public showBanner = false;
  public isStudentApp = false;

  @ViewChild('bannerEl') private readonly bannerEl!: ElementRef<HTMLElement>;

  private previouslyFocusedElement: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private readonly cookieConsentService = inject(CookieConsentService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  public constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.isStudentApp = this.document.location.pathname.split('/').includes(STUDENT_ROUTE.name);
      });
  }

  public ngOnInit(): void {
    this.showBanner = this.cookieConsentService.requiresAcknowledgement();
    this.isStudentApp = this.document.location.pathname.split('/').includes(STUDENT_ROUTE.name);
  }

  public ngAfterViewInit(): void {
    if (this.showBanner) {
      this.previouslyFocusedElement = this.document.activeElement as HTMLElement;
      this.bannerEl.nativeElement.showPopover();
      this.document.body.classList.add('kh-cookie-banner-active');
      this.bannerEl.nativeElement.focus();
      this.observeBannerHeight();
    }
  }

  public ngOnDestroy(): void {
    this.stopObservingBannerHeight();
    this.document.body.style.removeProperty('--kh-cookie-banner-height');
  }

  public onAcknowledge(): void {
    this.cookieConsentService.saveAcknowledgement();
    this.bannerEl.nativeElement.hidePopover();
    this.showBanner = false;
    this.document.body.classList.remove('kh-cookie-banner-active');
    this.stopObservingBannerHeight();
    this.document.body.style.removeProperty('--kh-cookie-banner-height');
    this.previouslyFocusedElement?.focus();
  }

  private observeBannerHeight(): void {
    const el = this.bannerEl.nativeElement;
    const update = () => {
      this.document.body.style.setProperty('--kh-cookie-banner-height', `${el.offsetHeight}px`);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(el);
    }
  }

  private stopObservingBannerHeight(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
